import { Box } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";
import { useMap } from "react-map-gl/maplibre";

import { HANDLE_CLASS_NAME } from "@/components/rana-resizable";
import { RanaResizable, RanaResizeCallback, ResizeDirection } from "@/components/rana-resizable";

import { EXPORT_MAP_ID, MIN_CAPTURE_AREA_SIZE } from "../consts";
import { CaptureArea } from "../types/index";

interface DraggableCaptureAreaProps {
  captureArea: CaptureArea;
  updateCaptureArea: (patch: Partial<CaptureArea>) => void;
  updateCaptureAreaFromDelta: (
    startCaptureArea: CaptureArea,
    direction: ResizeDirection,
    delta: { width: number; height: number }
  ) => void;
}

export const DraggableCaptureArea: React.FC<DraggableCaptureAreaProps> = ({
  captureArea,
  updateCaptureArea,
  updateCaptureAreaFromDelta,
}) => {
  const { [EXPORT_MAP_ID]: map } = useMap();
  const resizeStartRef = useRef<CaptureArea | null>(null);
  const isResizingRef = useRef(false);

  // Cleanup function for drag event listeners
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const passWheelToMap = (e: React.WheelEvent<HTMLElement>) =>
    map?.getCanvas()?.dispatchEvent(new WheelEvent("wheel", e.nativeEvent));

  const handleDragStart = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (isResizingRef.current) return;
    const target = e.target as HTMLElement;
    if (target.closest(`.${HANDLE_CLASS_NAME}`)) return;

    e.preventDefault();
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startX = captureArea.x;
    const startY = captureArea.y;

    const handleMouseMove = (ev: MouseEvent) => {
      const x = startX + ev.clientX - startMouseX;
      const y = startY + ev.clientY - startMouseY;
      updateCaptureArea({ x, y });
    };

    const cleanupListeners = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", cleanupListeners);
      dragCleanupRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", cleanupListeners);
    dragCleanupRef.current = cleanupListeners;
  };

  const handleResizeStart = () => {
    isResizingRef.current = true;
    resizeStartRef.current = { ...captureArea };
  };

  const handleResizeFromDelta = (
    direction: ResizeDirection,
    delta: { width: number; height: number }
  ) => {
    const start = resizeStartRef.current;
    if (!start) return;
    updateCaptureAreaFromDelta(start, direction, delta);
  };

  const handleResize: RanaResizeCallback = (_e, direction, _ref, delta) => {
    handleResizeFromDelta(direction, delta);
  };

  const handleResizeStop: RanaResizeCallback = (_e, direction, _ref, delta) => {
    handleResizeFromDelta(direction, delta);
    isResizingRef.current = false;
  };

  return (
    <RanaResizable
      style={{
        pointerEvents: "all",
        position: "absolute",
        left: `${Math.max(captureArea.x, 0)}px`,
        top: `${Math.max(captureArea.y, 0)}px`,
        zIndex: 2,
      }}
      size={captureArea}
      minWidth={MIN_CAPTURE_AREA_SIZE}
      minHeight={MIN_CAPTURE_AREA_SIZE}
      onResizeStart={handleResizeStart}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
    >
      <Box
        cursor="grab"
        onMouseDown={handleDragStart}
        onWheel={passWheelToMap}
        w="full"
        h="full"
        borderWidth="base"
        borderStyle="dashed"
        borderColor="brand.analytic"
        display="flex"
        alignItems="center"
        justifyContent="center"
        userSelect="none"
        sx={{ outline: "100vmax solid", outlineColor: "blackAlpha.500" }}
      />
    </RanaResizable>
  );
};
